import { useEffect } from "react";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Capacitor } from "@capacitor/core";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { OfflineProvider } from "@/offline/OfflineProvider";
import { PrivacyConsentGate } from "@/components/auth/PrivacyConsentGate";
import { ForceUpdateGate } from "@/components/app/ForceUpdateGate";
import { AndroidBackButton } from "@/components/common/AndroidBackButton";
import { BootSplash } from "@/components/app/BootSplash";
import { SyncProgressBar } from "@/components/layouts/SyncProgressBar";
import { AppRouter } from "@/routes";
import { ApiError } from "@/lib/api-client";
import { env } from "@/lib/env";
import { isLifecycleQuery } from "@/lib/query-invalidation";

// Native toast offset. On native the WebView draws into the status-bar strip
// (Android opaque bar; iOS edge-to-edge under the notch), so sonner's default
// 16px top offset drops toasts into it. Push them below the inset on both; web
// keeps sonner's defaults. env(safe-area-inset-top) is 0 on Android, so floor
// at 2rem; iOS reports the real notch.
const androidToastOffset =
  Capacitor.isNativePlatform() ? { top: "max(env(safe-area-inset-top), 2rem)" } : undefined;

const queryClient = new QueryClient({
  // Coherence net: after ANY successful mutation, refresh every lifecycle query
  // (inspections / observations / findings / rectifications and their derived
  // views) so all pages reflect the change without a manual refresh. The same
  // entity is read under several query-key namespaces across pages, so we match
  // the whole family by prefix instead of relying on each mutation to list keys.
  // Active queries refetch now; inactive ones refetch on next mount.
  mutationCache: new MutationCache({
    onSuccess: (_data, _vars, _ctx, mutation) => {
      // Batched answer-editing mutations opt out (meta.skipLifecycleInvalidate):
      // handleSaveQuestion runs many of them and does its own single refresh, so
      // a per-step family invalidation would refetch N times and race that seed.
      if (mutation.options.meta?.skipLifecycleInvalidate) return;
      void queryClient.invalidateQueries({ predicate: isLifecycleQuery });
    },
  }),
  defaultOptions: {
    queries: {
      // ALWAYS run the queryFn, even when navigator.onLine is false. React
      // Query's default ("online") PAUSES queries while offline, so the queryFn
      // never runs — which means our readThrough never gets to serve the Dexie
      // cache and offline screens come up empty. readThrough itself decides
      // online-vs-cache, so we want it invoked unconditionally.
      networkMode: "always",
      // Collaborative data (inspections / observations / rectifications /
      // findings / checklists) is created and changed by OTHER users in other
      // sessions, which this client can't be invalidated about. So treat data as
      // stale immediately and re-fetch in the BACKGROUND whenever a page mounts
      // (navigation) or the window regains focus (e.g. switching back from
      // another window). Cached data is shown instantly meanwhile (gcTime), so
      // there's no loading flash — the screen just updates when the response
      // lands, no manual refresh needed. Queries that genuinely benefit from
      // caching (question library, reference data) set their own higher
      // staleTime locally and are unaffected by this default.
      staleTime: 0,
      gcTime: 5 * 60_000,
      retry: (failureCount, err) => {
        // Don't retry 4xx (other than network/timeouts)
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: true,
    },
    mutations: {
      // Run mutationFns even when navigator.onLine is false. React Query's
      // default ("online") PAUSES mutations offline — the button shows pending
      // forever and the mutationFn never runs. Our offline-aware writes
      // (writeOrQueue) decide network-vs-queue themselves, so they must be
      // invoked unconditionally, exactly like the queries above.
      networkMode: "always",
      retry: 0,
    },
  },
});

export default function App() {
  // iOS (WKWebView + Safari): the native scroll view's pan gesture
  // (rubber-band bounce) steals touch drags before sonner's pointer events
  // see them, so toasts couldn't be swiped away on iPhones — the page
  // bounced instead and the toast got a pointercancel. Sonner already sets
  // `touch-action: none` on toasts, but WKWebView doesn't reliably honour it
  // for the main-frame scroll view. A NON-PASSIVE touchmove listener that
  // preventDefault()s only for touches on a toast blocks the native scroll
  // synchronously and lets the swipe-to-dismiss gesture run. No-op on
  // platforms where swiping already works (they respect touch-action).
  useEffect(() => {
    const blockNativeScrollOnToastSwipe = (e: TouchEvent) => {
      if (!e.cancelable) return;
      const target = e.target as Element | null;
      if (target?.closest?.("[data-sonner-toast]")) e.preventDefault();
    };
    document.addEventListener("touchmove", blockNativeScrollOnToastSwipe, { passive: false });
    return () => document.removeEventListener("touchmove", blockNativeScrollOnToastSwipe);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          {/* Native boot splash: holds the shield until auth bootstrap finishes
              so iOS matches Android's launch (no white screen + spinner gap). */}
          <BootSplash />
          <OfflineProvider>
            <BrandingProvider>
              {/* Thin top progress bar while a sync / offline warm-up runs. */}
              <SyncProgressBar />
              {/* First-launch privacy consent runs before any route (native only). */}
              <PrivacyConsentGate>
                {/* Update gate: overlays a blocking force-update screen when the
                    build is below the enforced floor; soft-prompts otherwise.
                    Optimistic (never delays render) + native-only + fail-open. */}
                <ForceUpdateGate>
                  {/* Android "double back to exit" — mounted only once consent is
                      accepted (the gate renders its own back listener before that),
                      so the two never fight over the hardware back button. */}
                  <AndroidBackButton />
                  <AppRouter />
                </ForceUpdateGate>
              </PrivacyConsentGate>
              <Toaster
                position="top-right"
                // Android-only: clear the status bar (see androidToastOffset above).
                // undefined on iOS/web keeps sonner's defaults, which work well there.
                offset={androidToastOffset}
                mobileOffset={androidToastOffset}
                theme="system"
                closeButton
                richColors
                toastOptions={{ classNames: { toast: "border border-border" } }}
              />
            </BrandingProvider>
          </OfflineProvider>
        </AuthProvider>
      </ThemeProvider>
      {env.rqDevtools && env.isDev ? <ReactQueryDevtools initialIsOpen={false} /> : null}
    </QueryClientProvider>
  );
}
